/**
 * TranscriptMaterialsButton (KAN-721)
 *
 * Button "Материалы" next to the dialog input: opens a list of the operator's
 * materials allowed in chats (price list, catalogs, product photos) with
 * "Отправить" per item. Sending posts the material into the client's dialog;
 * toast "Отправлено" or the backend detail.
 *
 * operatorId: operator of the dialog's account; null when unknown, then the
 * list holds the materials of all operators visible to the current user and
 * every item names its operator.
 *
 * KAN-736: a compact button in the input row next to the paperclip (icon only
 * on narrow screens); the list opens upwards over the feed.
 *
 * KAN-811: not shown when the materials list is closed to the user (rules of
 * the MANAGER role from /auth/me).
 */
import { useId, useState } from 'react'
import { FolderOpen, Loader2, Send } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { MaterialPreview } from '@/components/users/MaterialPreview'
import { formatFileSize, isImageMaterial } from '@/components/users/materialFiles'
import { useSendableMaterials, useSendMaterial } from '@/hooks/useInstructionAttachments'
import { useCanCallApi } from '@/hooks/useApiAccess'

interface TranscriptMaterialsButtonProps {
  clientId: string
  operatorId?: string | null
}

export function TranscriptMaterialsButton({
  clientId,
  operatorId = null,
}: TranscriptMaterialsButtonProps) {
  const [open, setOpen] = useState(false)
  const panelId = useId()
  const { data: items = [], isLoading, isError } = useSendableMaterials(operatorId, open)
  const send = useSendMaterial(clientId)
  const operators = new Set(items.map((item) => item.operator_id))
  const showOperator = !operatorId && operators.size > 1
  const allowed = useCanCallApi('GET', '/api/v1/instruction-attachments/')
  if (!allowed) return null

  return (
    <div className="relative shrink-0" data-testid="transcript-materials">
      <Button
        type="button"
        size="sm"
        variant="outline"
        aria-label="Материалы"
        title="Материалы"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
        className="h-10 gap-1 px-2.5"
      >
        <FolderOpen className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">Материалы</span>
      </Button>
      {open && (
        <div
          id={panelId}
          role="region"
          aria-label="Материалы оператора"
          className="absolute bottom-full left-0 z-20 mb-2 max-h-72 w-[min(22rem,calc(100vw-7rem))] overflow-y-auto rounded-md border bg-white p-2 shadow-lg"
        >
          {isLoading && (
            <Loader2 aria-label="Загрузка" className="h-4 w-4 animate-spin text-gray-500" />
          )}
          {isError && (
            <p className="text-xs text-red-600">Не удалось загрузить материалы</p>
          )}
          {!isLoading && !isError && items.length === 0 && (
            <p className="text-xs text-gray-500">
              Нет материалов: добавьте их в профиле оператора
            </p>
          )}
          {items.length > 0 && (
            <ul className="space-y-2">
              {items.map((item) => {
                const size = isImageMaterial(item) ? '' : formatFileSize(item.size_bytes)
                const sending = send.isPending && send.variables === item.id
                return (
                  <li
                    key={item.id}
                    data-testid={`transcript-material-${item.id}`}
                    className="flex items-center gap-2"
                  >
                    <MaterialPreview item={item} className="h-10 w-10" />
                    <div className="min-w-0 flex-1 text-sm">
                      <div className="flex flex-wrap items-center gap-1">
                        <span className="break-words font-medium">{item.title}</span>
                        {item.is_main_price && (
                          <Badge variant="completed">Основной прайс</Badge>
                        )}
                      </div>
                      {(size || showOperator) && (
                        <p className="truncate text-xs text-gray-500">
                          {[showOperator ? item.operator_email : '', size]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      )}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      aria-label={`Отправить ${item.title}`}
                      disabled={send.isPending}
                      onClick={() => send.mutate(item.id)}
                    >
                      {sending ? (
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="mr-1 h-4 w-4" aria-hidden="true" />
                      )}
                      Отправить
                    </Button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
